import { useCallback, useEffect, useMemo, useState } from "react";
import { ChatMessage } from "~/api/model";
import { useChatDocumentParams } from "~/helpers";
import { ChatMessageResponse } from "~/lib/wss-client";
import { useWebsocket } from "./useWebsocket";

type UseWebSocketChatMessageReturn = {
  sendMessage: (message: string) => void;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
};

type UseWebSocketChatMessageOptions = {
  onChatResponse?: (response: ChatMessageResponse) => void;
};

export const useWebSocketChatMessage = (
  options?: UseWebSocketChatMessageOptions
): UseWebSocketChatMessageReturn => {
  const { organizationId, caseId, chatId } = useChatDocumentParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsParams = useMemo(
    () => ({
      projectId: caseId,
      subscribeTo: "chat-message" as const,
      chatId,
    }),
    [caseId, chatId]
  );

  const { isConnected, addChatListener, removeChatListener, sendChatMessage } =
    useWebsocket<ChatMessage>(wsParams);

  const handleChatResponse = useCallback(
    (response: ChatMessageResponse) => {
      setIsLoading(false);

      if (response.action === "message-error") {
        setError(response.error || "Unknown error occurred");
      } else if (response.action === "message-response") {
        setError(null);
        // Call external handler if provided
        options?.onChatResponse?.(response);
      }
    },
    [options]
  );

  useEffect(() => {
    addChatListener(handleChatResponse);
    return () => {
      removeChatListener(handleChatResponse);
    };
  }, [addChatListener, removeChatListener, handleChatResponse]);

  const sendMessage = useCallback(
    (message: string) => {
      if (!isConnected) {
        setError("WebSocket is not connected");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        sendChatMessage(organizationId, message);
      } catch (err) {
        setIsLoading(false);
        setError(err instanceof Error ? err.message : "Failed to send message");
      }
    },
    [isConnected, organizationId, sendChatMessage]
  );

  return {
    sendMessage,
    isConnected,
    isLoading,
    error,
  };
};
